import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, PlayCircle, UserRound, FileQuestion, Phone, MapPin, MessageCircle, ArrowRight } from "lucide-react";
import { api, useSeo, waLink, fmtDate } from "../lib/api";
import { useSite } from "../context/AppContext";
import { Section, CtaLink, Empty, Text } from "../components/Common";
import { CourseCard, EventRow } from "../components/Cards";

const HERO = "https://images.unsplash.com/photo-1652739758426-56a564265f9e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";

function Hero({ s }) {
  return (
    <section className="relative bg-zinc-950 text-white overflow-hidden" data-testid="home-hero">
      <img src={HERO} alt="Özel güvenlik görevlisi eğitimi" className="absolute inset-0 w-full h-full object-cover opacity-35" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
        <span className="eyebrow fade-up" data-testid="hero-slogan">{s.slogan || "PROFESYONEL EĞİTİM • GÜNCEL BİLGİ • UYGULAMALI EĞİTİM"}</span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl leading-[1.05] fade-up" style={{ animationDelay: "80ms" }} data-testid="hero-title">
          {s.hero_title || "Geleceğinizi Güvenle İnşa Edin"}
        </h1>
        <p className="mt-6 max-w-xl text-zinc-300 text-sm sm:text-base leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>{s.hero_text}</p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up" style={{ animationDelay: "240ms" }}>
          <CtaLink to="/egitimlerimiz" variant="outline" testid="hero-courses-btn" className="text-white">Kursları İncele</CtaLink>
          <CtaLink to="/kurs-basvurusu" testid="hero-apply-btn">Hemen Başvur</CtaLink>
          <CtaLink to="/deneme-sinavlari" variant="outline" testid="hero-exam-btn" className="text-white">Deneme Sınavını Çöz</CtaLink>
        </div>
      </div>
      <div className="relative border-t border-white/10 bg-zinc-950/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10 text-sm">
          {["Silahlı & silahsız temel eğitim", "Yenileme ve silah farkı programları", "Online deneme sınavı ve video dersler"].map((t) => (
            <div key={t} className="py-4 sm:px-6 first:sm:pl-0 flex items-center gap-3 text-zinc-300"><span className="w-1.5 h-1.5 bg-[#dc2626]" />{t}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OnlineArea() {
  const items = [
    { to: "/deneme-sinavlari", icon: FileQuestion, t: "Deneme sınavına başla", d: "Üyeliksiz ücretsiz örnek sınavı hemen çözün.", id: "online-exam" },
    { to: "/egitim-videolari", icon: PlayCircle, t: "Eğitim videoları", d: "Ders videolarını kategorilere göre inceleyin.", id: "online-videos" },
    { to: "/kursiyer-girisi", icon: UserRound, t: "Kursiyer paneli", d: "Başvuru, sınav sonucu ve ilerleme takibi.", id: "online-panel" },
    { to: "/kurs-basvurusu", icon: ClipboardCheck, t: "Online başvuru", d: "Birkaç dakikada ön başvurunuzu iletin.", id: "online-apply" },
  ];
  return (
    <section className="bg-[#dc2626] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-8 lg:grid-cols-[1fr_2fr] items-center">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-100">Online Eğitim Alanı</span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-black">Sınava hazırlığınızı her yerden sürdürün</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-red-400/40">
          {items.map(({ to, icon: I, t, d, id }) => (
            <Link key={to} to={to} className="bg-[#dc2626] hover:bg-[#b91c1c] p-5 flex gap-4 group" style={{ transition: "background-color .2s" }} data-testid={id}>
              <I className="shrink-0" /><span><span className="font-bold flex items-center gap-2">{t}<ArrowRight size={14} className="group-hover:translate-x-1" style={{ transition: "transform .2s" }} /></span><span className="block text-sm text-red-100 mt-1">{d}</span></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Trust({ s, instructors }) {
  const f = s.founder || {};
  return (
    <Section eyebrow="Kurumsal Güven" title="Neden Ada?" className="!pt-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 card-ada p-6 sm:p-8">
          <h3 className="font-black text-xl mb-3">Kurum hakkında</h3>
          <Text value={s.about} fallback="[Kurum tanıtımı eklenecek]" />
          <h3 className="font-black text-lg mt-6 mb-2">Eğitim yaklaşımımız</h3>
          <Text value={s.approach} fallback="[Eğitim yaklaşımı eklenecek]" />
          <Link to="/kurumsal" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#dc2626]" data-testid="home-about-link">Kurumsal sayfaya git <ArrowRight size={14} /></Link>
        </div>
        <div className="bg-zinc-950 text-white p-6 sm:p-8 flex flex-col">
          <span className="eyebrow">Kurucumuz</span>
          <div className="mt-4 w-20 h-20 bg-zinc-800 flex items-center justify-center overflow-hidden">
            {f.photo ? <img src={f.photo} alt={f.name} className="w-full h-full object-cover" /> : <UserRound className="text-zinc-500" size={32} />}
          </div>
          <h3 className="mt-4 text-2xl font-black" data-testid="home-founder-name">{f.name || "Tolga Ünal"}</h3>
          <p className="text-sm text-zinc-400">{f.title}</p>
          <div className="mt-auto pt-6 flex gap-4 text-sm">
            <Link to="/egitmenlerimiz" className="font-bold text-[#ef4444]" data-testid="home-instructors-link">Eğitmenlerimiz ({instructors.length})</Link>
            <Link to="/sss" className="font-bold text-zinc-300" data-testid="home-faq-link">SSS</Link>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ContactStrip({ s }) {
  return (
    <section className="bg-zinc-100 border-t border-zinc-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-6 md:grid-cols-3">
        <a href={`tel:+9${(s.phone || "").replace(/\D/g, "")}`} className="flex gap-3 items-start" data-testid="home-call"><Phone className="text-[#dc2626]" /><span><b className="block">Bizi arayın</b>{s.phone}</span></a>
        <a href={waLink()} target="_blank" rel="noreferrer" className="flex gap-3 items-start" data-testid="home-whatsapp"><MessageCircle className="text-[#25D366]" /><span><b className="block">WhatsApp</b>Hızlı bilgi alın</span></a>
        <Link to="/iletisim" className="flex gap-3 items-start" data-testid="home-contact"><MapPin className="text-[#dc2626]" /><span><b className="block">Adres</b><span className="text-sm">{s.address}</span></span></Link>
      </div>
    </section>
  );
}

export default function Home() {
  const s = useSite();
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [instructors, setInstructors] = useState([]);
  useSeo("", s.seo?.description);
  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data));
    api.get("/events", { params: { start: new Date().toISOString().slice(0, 10) } }).then((r) => setEvents(r.data.slice(0, 4)));
    api.get("/posts", { params: { kind: "announcement", limit: 3 } }).then((r) => setPosts(r.data));
    api.get("/instructors").then((r) => setInstructors(r.data));
  }, []);
  return (
    <>
      <Hero s={s} />
      <Section eyebrow="Eğitim Kategorileri" title="Eğitimlerimiz" text="Mevzuata uygun programlarımızı inceleyin; süre, şart ve ücret bilgileri yönetici onayıyla güncellenir."
        action={<CtaLink to="/egitimlerimiz" variant="dark" testid="home-all-courses">Tüm Eğitimler</CtaLink>}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {courses.map((c, i) => <div key={c.id} className={i === 0 ? "sm:col-span-2 lg:row-span-2 [&>a]:h-full" : ""}><CourseCard c={c} i={i} big={i === 0} /></div>)}
        </div>
      </Section>
      <Section eyebrow="Eğitim Takvimi" title="Yaklaşan programlar ve tarihler" className="!pt-0"
        action={<CtaLink to="/egitim-takvimi" variant="dark" testid="home-calendar-link">Takvimi Aç</CtaLink>}>
        <div className="grid gap-3">{events.length ? events.map((e) => <EventRow key={e.id} e={e} />) : <Empty text="Yaklaşan program bulunmuyor." />}</div>
        <p className="mt-3 text-xs text-zinc-500">Sınav tarihleri resmî olarak duyurulana kadar "tarih kesinleşmedi" olarak gösterilir.</p>
      </Section>
      <OnlineArea />
      <Trust s={s} instructors={instructors} />
      <Section eyebrow="Duyurular" title="Son duyurular" className="!pt-0" action={<CtaLink to="/duyurular" variant="dark" testid="home-news-link">Tüm Duyurular</CtaLink>}>
        <div className="grid gap-4 md:grid-cols-3">
          {posts.length ? posts.map((p) => (
            <Link key={p.id} to={`/duyurular/${p.slug}`} className="card-ada p-6 block" data-testid={`home-post-${p.slug}`}>
              <span className="text-xs text-zinc-500">{fmtDate(p.published_at)}</span>
              <h3 className="mt-2 font-black text-lg">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-600 line-clamp-3">{p.excerpt}</p>
            </Link>
          )) : <Empty text="Henüz duyuru yok." />}
        </div>
      </Section>
      <ContactStrip s={s} />
    </>
  );
}
